import { PRODUCT_LECTURA, PRODUCT_READING } from 'root/lib/constants';
import {
  getBucketData,
  getListOfSupportedGames,
  findChapterName,
  isGameIncompleteAndDifferentProduct,
  formatPlacement,
} from 'lib/utilityFunctions';
import readBucketData, { getQuestsData } from '../bucketsMigration';
import gameIds from '../../data/GameIds';
import mapLocations from '../../data/MapLocations';
import STEP_TYPE from '../../data/StepTypes';
import { SEASON } from '../../data/constants';
import { getShouldRePlacementModalBeShown } from '../helper';
import initialQuestState, { questStateForDemo, questStateForLecturaDemo } from '../state/questState';
import MapQuestsLectura from '../../data/MapQuestsLectura.json';

// function to merge two objects when new location are released
const mergeLocations = (stateLocations, savedLocations) =>
  ({
    ...stateLocations,
    ...savedLocations || {},
  });

const updateChallengeQuestEreaderProgress = (challengeQuestsState, ereader) => {
  const challengeQuestsCopy = {
    ...challengeQuestsState,
  };
  (challengeQuestsCopy || { steps: [] })
    .steps
    .forEach(getUpdateEreaderStepProgressFunction(challengeQuestsCopy.steps, ereader));
  return challengeQuestsCopy;
};

const getUpdateEreaderStepProgressFunction = (stepsList, ereader) => (step, index) => {
  if (!step) return;
  const { type, game, levels } = step;
  if (type === STEP_TYPE.EREADER) {
    const chapter = findChapterName(game, levels[0]);
    const { GameData: { gameDetails } = {} } = ereader[game];
    if (gameDetails) {
      const { questOverride, status } = gameDetails;
      let eReaderStatus;
      if (chapter) {
        // chapter progress
        if (questOverride && questOverride[chapter]) {
          eReaderStatus = questOverride[chapter].status;
        }
      } else {
        // full book progress
        eReaderStatus = status;
      }
      // when ereader book is complete we set levelsMastered equal to levels to consider step completed
      if (eReaderStatus === 'complete') {
        // eslint-disable-next-line no-param-reassign
        stepsList[index].levelsMastered = [...stepsList[index].levels];
      }
    }
  }
};

const updateCurrentQuestEreaderProgress = (questsState, currentActiveQuestId, ereader) => {
  const questsCopy = {
    ...questsState,
  };
  if (currentActiveQuestId) {
    const questName = `quest${currentActiveQuestId}`;
    (questsState[questName] || { steps: [] })
      .steps
      .forEach(getUpdateEreaderStepProgressFunction(questsCopy[questName].steps, ereader));
    return questsCopy;
  }
  return {};
};

const updateCurrentQuestVocabProgress = (questsState, currentActiveQuestId, vocab) => {
  const questsCopy = {
    ...questsState,
  };
  if (currentActiveQuestId) {
    const questName = `quest${currentActiveQuestId}`;
    (questsState[questName] || { steps: [] }).steps.forEach((step, index) => {
      const { type } = step;
      if (type === STEP_TYPE.VOCAB) {
        const { GameData: { questProgress } = {} } = vocab;
        if (questProgress && questProgress[currentActiveQuestId]) {
          if (questProgress[currentActiveQuestId] === 'finished') {
            questsCopy[questName].steps[index].levelsMastered = [...questsCopy[questName].steps[index].levels];
          }
        }
      }
    });
    return questsCopy;
  }
  return {};
};

const setInitialMetagameData = (season) => {
  const defaultData = {
    currentActiveLocation: mapLocations.SCHOOL,
    contentServiceQuestId: 0,
    didEndOfQuestsPlay: false,
    isPostQ1InteractionEnabled: false,
    isNotesTabVisible: false,
    showChallengeQuestPopup: {
      [PRODUCT_READING]: false,
      [PRODUCT_LECTURA]: false,
    },
    lastQuestFinishedBeforeEndOfQuests: undefined,
    observationApiFailures: {},
    writeResultFailures: {},
  };

  switch (season) {
    case SEASON.GRADE_K_1:
      return {
        ...defaultData,
        currentActiveQuestId: 1,
      };
    case SEASON.GRADE_2_3:
      return {
        ...defaultData,
        currentActiveQuestId: 201,
      };
    default:
      return {};
  }
};
const getSeasonMapAreas = (season) => {
  switch (season) {
    case SEASON.GRADE_K_1:
      return {
        ...initialQuestState.mapAreas,
        [mapLocations.SCHOOL]: {
          ...initialQuestState.mapAreas[mapLocations.SCHOOL],
          defaultQuestForLocation: 1,
        },
      };
    case SEASON.GRADE_2_3:
      return {
        ...initialQuestState.mapAreas,
        [mapLocations.SCHOOL]: {
          ...initialQuestState.mapAreas[mapLocations.SCHOOL],
          defaultQuestForLocation: 201,
        },
      };
    default:
      return initialQuestState.mapAreas;
  }
};

// only validates season 1 and 2
const isActiveQuestIdValidForSeason = (questId, season) => {
  if (season !== SEASON.GRADE_4_5) { // season 3 doesn't update currentActiveQuestId
    if (questId > 0 && questId <= 24 && season !== SEASON.GRADE_K_1) {
      // quest data and season mismatch
      return false;
    }
    if (questId > 200 && questId <= 224 && season !== SEASON.GRADE_2_3) {
      // quest data and season mismatch
      return false;
    }
  }
  return true;
};

const didSeasonSwitch = (localSeasonValue, savedSeasonValue, savedActiveQuestId) => {
  if (!savedSeasonValue || !localSeasonValue) {
    // If we don't have and old and new season values to compare, assume the season has not changed
    return false;
  }
  // test if Season in user settings differs from Season we worked with before
  if (savedSeasonValue !== localSeasonValue) {
    // looks like we have Season switched so we have to update quests data
    return true;
  }
  if (savedActiveQuestId === undefined) {
    return true;
  }
  // we consider a non valid activeQuest/season match as a flag for quest switch (for proper initialization)
  return !isActiveQuestIdValidForSeason(savedActiveQuestId, localSeasonValue);
};

const validateMapAreasData = (mapAreas, questId, location) => {
  const localMapAreas = { ...mapAreas };
  if (questId) {
    // check if current quest location is opened
    if (localMapAreas[location].locationOpened) {
      // location is opened - GOOD
      // check if default quest for that location is correct
      if (localMapAreas[location].defaultQuestForLocation !== parseInt(questId, 10)) {
        localMapAreas[location].defaultQuestForLocation = parseInt(questId, 10);
      }
    } else {
      // mapAreas mismatch current quest data - fix it
      localMapAreas[location].locationOpened = true;
      localMapAreas[location].defaultQuestForLocation = parseInt(questId, 10);
      localMapAreas[location].locationProgress = 0;
      localMapAreas[location].lastStepToAnimate = '';
      // disable other map locations
      Object.values(mapLocations).forEach((loc) => {
        if (loc !== location) {
          localMapAreas[loc].locationOpened = false;
        }
      });
    }
  }
  return localMapAreas;
};

const getDemoSeason = state =>
  ((state[gameIds.TEACHER_DASHBOARD_SETTINGS] || {}).GameData || {}).demoSeason;

const getIsDemoSeason = state => [SEASON.GRADE_K_1, SEASON.GRADE_2_3, SEASON.GRADE_4_5].includes(getDemoSeason(state));

export const mergeServerAndLocalData = (state, action) => {
  const {
    placementData: { placedMethod, placementPeriod, placementGrade },
    stateData: allPersistentStateData,
    product,
  } = action;
  let localState = { ...state };
  let { season } = localState;
  const bucketData = getBucketData(allPersistentStateData[gameIds.METAGAME]);
  const hasIncomingData = !!bucketData.gameDetails;
  let incomingMetagameBucketData = bucketData.gameDetails || {};
  let { seasonSaved } = incomingMetagameBucketData;
  const placement = formatPlacement(placementPeriod, placementGrade);
  const isDemoSeason = getIsDemoSeason(allPersistentStateData);

  if (product === PRODUCT_LECTURA) {
    initialQuestState.quests = MapQuestsLectura.quests;
  }

  if (isDemoSeason) {
    const demoSeason = getDemoSeason(allPersistentStateData);
    // override state with hardcoded Demo state
    localState = questStateForDemo[demoSeason];
    if (product === PRODUCT_LECTURA) {
      localState = questStateForLecturaDemo[demoSeason];
    }
    season = demoSeason;
    seasonSaved = demoSeason;
  } else if (!season && seasonSaved) {
    season = seasonSaved;
  } else if (!season && !seasonSaved) {
    throw new Error('Invalid Season data');
  }

  seasonSaved = seasonSaved ? seasonSaved.toString() : seasonSaved;
  season = season ? season.toString() : season;

  const isLocalQuestValidForSeason = isActiveQuestIdValidForSeason(localState.currentActiveQuestId, localState.season);

  const shouldInitializeMetagameData = !isDemoSeason
    && (didSeasonSwitch(
      season,
      seasonSaved,
      incomingMetagameBucketData.currentActiveQuestId,
    ) || (!hasIncomingData && !isLocalQuestValidForSeason)); // if no incoming data we still need to flag a reset if local data is not valid

  if (shouldInitializeMetagameData) {
    incomingMetagameBucketData = setInitialMetagameData(season);
  }

  // get full metagame state by merging localState and incomingMetagameBucketData
  const metagameBucketData = {
    ...localState,
    ...incomingMetagameBucketData,
  };

  const {
    currentActiveQuestId,
    currentActiveLocation,
    bitesEarnedInQuest,
    didEndOfQuestsPlay,
    isPostQ1InteractionEnabled,
    unlockedGlyphPowers,
    isNotesTabVisible,
    placement: savedPlacement,
    lastQuestFinishedBeforeEndOfQuests: lastQuestFinished,
  } = metagameBucketData;

  const questsBucketData = shouldInitializeMetagameData ? initialQuestState : getQuestsData(
    gameIds.METAGAME_QUEST_CURRENT,
    gameIds.METAGAME_QUEST_HISTORY,
    action,
  );

  const currentQuestBucket = getBucketData(action.stateData[gameIds.METAGAME_QUEST_CURRENT]).gameDetails || {};

  let readingQuestStepsCache = shouldInitializeMetagameData ?
    [] : currentQuestBucket.readingQuestStepsCache || localState.readingQuestStepsCache || [];
  let lecturaQuestStepsCache = shouldInitializeMetagameData ?
    [] : currentQuestBucket.lecturaQuestStepsCache || localState.lecturaQuestStepsCache || [];

  const vocabBucketData = getBucketData(allPersistentStateData[gameIds.VOCAB], true);

  const ereaderBookData = { };
  Object.keys(allPersistentStateData).filter(game => game.includes('ereader')).forEach((book) => {
    ereaderBookData[book] = getBucketData(allPersistentStateData[book], true);
  });

  readingQuestStepsCache.forEach(getUpdateEreaderStepProgressFunction(readingQuestStepsCache, ereaderBookData));
  lecturaQuestStepsCache.forEach(getUpdateEreaderStepProgressFunction(lecturaQuestStepsCache, ereaderBookData));

  const {
    challengeQuest = { steps: [] },
    areAllQuestsAndChallengesComplete,
  } = ((allPersistentStateData[gameIds.METAGAME_CHALLENGE_QUEST] || {}).GameData || {})
    .gameDetails || {
    areAllQuestsAndChallengesComplete: {},
  };

  const mergedQuests = { ...localState.quests, ...questsBucketData.quests };

  const currentQuestKey = `quest${currentActiveQuestId}`;
  if (currentActiveQuestId !== 0) { // no quests to merge from
    const localQuestSteps = [...localState.quests[currentQuestKey].steps];
    let mergedQuestSteps = localQuestSteps;
    if (currentQuestBucket?.currentQuest?.[currentQuestKey]) {
      const currentQuest = updateCurrentQuestVocabProgress(
        currentQuestBucket.currentQuest,
        currentActiveQuestId,
        vocabBucketData,
      );
      const currentQuestBucketQuestSteps = currentQuest[currentQuestKey].steps;

      // Check for duplicate quest steps
      const uniqueQuestSteps = currentQuestBucketQuestSteps.reduce((acc, step) => {
        const index = acc.findIndex(accStep => step.game === accStep.game && step.skillId === accStep.skillId);
        if (index === -1) {
          acc.push(step);
        }
        return acc;
      }, []);

      mergedQuestSteps = [];
      for (let i = 0; i < localQuestSteps.length; i++) {
        if (i >= uniqueQuestSteps.length) {
          // Stopgap solution for fixing users' broken game state due to season change without clearing quest steps cache
          // If user moved from season 1 to 2, the user could have the wrong number of steps, making it impossible to finish the quest
          // In this case, clearing the quest steps cache and adding hard-coded steps to be replaced by adaptivy should fix the problem
          readingQuestStepsCache = [];
          lecturaQuestStepsCache = [];
          mergedQuestSteps.push(localQuestSteps[i]);
        } else if (isGameIncompleteAndDifferentProduct(uniqueQuestSteps[i], product)) {
          mergedQuestSteps.push(localQuestSteps[i]);
        } else {
          mergedQuestSteps.push(uniqueQuestSteps[i]);
        }
      }
    }
    if (!shouldInitializeMetagameData) {
      mergedQuests[`quest${currentActiveQuestId}`].steps = [...mergedQuestSteps];
    }
  }
  const finalQuests = shouldInitializeMetagameData ? initialQuestState.quests : mergedQuests;
  return {
    ...localState,
    currentActiveQuestId,
    currentActiveLocation,
    bitesEarnedInQuest,
    didEndOfQuestsPlay,
    isPostQ1InteractionEnabled,
    unlockedGlyphPowers,
    isNotesTabVisible,
    season,
    ereaderKeys: [...Object.keys(allPersistentStateData)].filter(data => data.includes('ereader')),
    quests: parseQuests(
      finalQuests,
      currentActiveQuestId,
      ereaderBookData,
      vocabBucketData,
      challengeQuest,
    ),
    mapAreas: shouldInitializeMetagameData ? getSeasonMapAreas(season) : validateMapAreasData(mergeLocations(
      localState.mapAreas, readBucketData(localState, action, 'mapAreas', gameIds.METAGAME_MAP_AREAS),
    ), currentActiveQuestId, currentActiveLocation),
    contentServiceQuestId: questsBucketData.contentServiceQuestId || localState.contentServiceQuestId,
    lastQuestFinishedBeforeEndOfQuests: lastQuestFinished,
    placement,
    placedMethod,
    isRePlacementModalShown: getShouldRePlacementModalBeShown(placement, savedPlacement, placedMethod),
    language: ((allPersistentStateData[gameIds.STUDENT_SETTINGS] || {}).GameData || {}).language || localState.language,
    captions: ((allPersistentStateData[gameIds.STUDENT_SETTINGS] || {}).GameData || {}).captions || localState.captions,
    showPracticeTab: localState.practiceEnabledLocally === undefined ?
      ((allPersistentStateData[gameIds.TEACHER_DASHBOARD_SETTINGS] || {}).GameData || {}).isPracticeMode
      || localState.showPracticeTab : localState.practiceEnabledLocally,
    stepFailures: readBucketData(localState, action, 'stepFailures', gameIds.METAGAME_QUEST_FAILURES),
    observationApiFailures: readBucketData(localState, action, 'observationApiFailures', gameIds.API_FAILURES),
    writeResultFailures: readBucketData(localState, action, 'writeResultFailures', gameIds.API_FAILURES),
    gamesData: {
      ...localState.gamesData,
      [gameIds.STUDENT_SETTINGS]: allPersistentStateData[gameIds.STUDENT_SETTINGS],
      [gameIds.TEACHER_DASHBOARD_SETTINGS]: allPersistentStateData[gameIds.TEACHER_DASHBOARD_SETTINGS],
      [gameIds.METAGAME_CURIOSO_SELECTED]: {
        ...getBucketData(allPersistentStateData[gameIds.METAGAME_CURIOSO_SELECTED], true),
      },
      [gameIds.METAGAME_USAGE]: {
        ...getBucketData(allPersistentStateData[gameIds.METAGAME_USAGE], true),
      },
      [gameIds.VOCAB]: { ...vocabBucketData },
      ...ereaderBookData,
    },
    sideQuest: {
      ...localState.sideQuest,
      ...readBucketData(localState, action, 'sideQuest', gameIds.METAGAME_SIDE_QUEST),
    },
    showChallengeQuestPopup: {
      [PRODUCT_READING]:
        !localState.userHadAllQuestsCompletePopupShown[PRODUCT_READING] &&
        currentActiveQuestId === 0 &&
        !areAllQuestsAndChallengesComplete[PRODUCT_READING],
      [PRODUCT_LECTURA]:
        !localState.userHadAllQuestsCompletePopupShown[PRODUCT_LECTURA] &&
        currentActiveQuestId === 0 &&
        !areAllQuestsAndChallengesComplete[PRODUCT_LECTURA],
    },
    showNewQuestsAvailablePopup:
      getShouldNewQuestPopupBeShown(currentActiveQuestId, season, lastQuestFinished, localState.newQuestReleased),
    areAllQuestsAndChallengesComplete: {
      [PRODUCT_READING]: areAllQuestsAndChallengesComplete[PRODUCT_READING] || false,
      [PRODUCT_LECTURA]: areAllQuestsAndChallengesComplete[PRODUCT_LECTURA] || false,
    },
    readingQuestStepsCache,
    lecturaQuestStepsCache,
  };
};

const validateReducerData = (state, action) => {
  const dataFromServer = mergeServerAndLocalData(state, action);
  const { currentActiveQuestId } = dataFromServer;
  // test if quest is not complete
  if (currentActiveQuestId) {
    dataFromServer.questSessionId = dataFromServer.quests[`quest${currentActiveQuestId}`].questSessionId;
    const questSteps = dataFromServer.quests[`quest${currentActiveQuestId}`].steps;
    const listOfSupportedGames = getListOfSupportedGames();
    for (let i = 0; i < questSteps.length; i++) {
      // test if game is disabled
      const game = questSteps[i].game.replace(/_es$/, '');
      if (questSteps[i].type === STEP_TYPE.GAME && !listOfSupportedGames.includes(game)) {
        // test if game is not completed
        if (questSteps[i].levels.length > questSteps[i].levelsMastered.length) {
          // autocomplete disabled game
          questSteps[i].levelsMastered = questSteps[i].levels;
          dataFromServer.autoCompletedGame = questSteps[i].game;
        }
      }
    }
    // update intro played to fix quest data
    questSteps.questIntroPlayed = questSteps.filter(step => step.levels <= step.levelsMastered).length;
  }
  return dataFromServer;
};

const parseQuests = (quests, id, ereader, vocab, challenge) => ({
  ...quests,
  ...updateCurrentQuestEreaderProgress(quests, id, ereader),
  ...updateCurrentQuestVocabProgress(quests, id, vocab),
  quest100: updateChallengeQuestEreaderProgress(challenge, ereader),
});

const getShouldNewQuestPopupBeShown = (currentQuestId, season, lastQuestFinished, newQuestReleased) => {
  if (season === SEASON.GRADE_2_3 && currentQuestId === 0 && newQuestReleased) {
    if (lastQuestFinished === undefined) {
      // notify user new quest is available
      return true;
    } if (lastQuestFinished >= newQuestReleased) {
      // most likely user already played that quest and finished all available quests
      return false;
    }
    return true;
  }
  // do not show New Quests popup
  return false;
};

export default validateReducerData;
